import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNavAccess } from "@/hooks/useNavAccess";
import { useOrgHref } from "@/hooks/useOrgParamSync";
import {
  LayoutDashboard,
  DollarSign,
  Server,
  Clock,
  Lightbulb,
  Key,
  FileText,
  Bell,
  LayoutGrid,
  Shield,
  Users,
  Settings,
  HelpCircle,
  Layers,
  UserCog,
  ClipboardCheck,
  BarChart3,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  keywords?: string[];
}

// eslint-disable-next-line react-refresh/only-export-components
export const MAIN_NAV: NavItem[] = [
  {
    name: "Dashboard",
    href: "/",
    icon: LayoutDashboard,
    keywords: ["home", "overview"],
  },
  {
    name: "Cost Analysis",
    href: "/costs",
    icon: DollarSign,
    keywords: ["costs", "spending", "billing"],
  },
  {
    name: "Inventory",
    href: "/inventory",
    icon: Server,
    keywords: ["resources", "instances", "vms"],
  },
  {
    name: "Scheduling",
    href: "/scheduling",
    icon: Clock,
    keywords: ["schedule", "start", "stop"],
  },
  {
    name: "Recommendations",
    href: "/recommendations",
    icon: Lightbulb,
    keywords: ["optimize", "savings", "rightsizing"],
  },
  {
    name: "Credentials",
    href: "/credentials",
    icon: Key,
    // "Cloud account" is the product's word for what this page lists.
    keywords: [
      "aws",
      "azure",
      "gcp",
      "cloud",
      "cloud accounts",
      "ai accounts",
      "subscriptions",
    ],
  },
  {
    name: "Reports",
    href: "/reports",
    icon: FileText,
    keywords: ["report", "export"],
  },
  {
    name: "Alerts",
    href: "/alerts",
    icon: Bell,
    keywords: ["notifications", "monitoring"],
  },
  {
    name: "Platform Hub",
    href: "/capabilities",
    icon: LayoutGrid,
    keywords: ["capabilities", "features"],
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings,
    keywords: ["preferences", "profile", "account"],
  },
  {
    name: "Help & Support",
    href: "/help",
    icon: HelpCircle,
    keywords: ["docs", "documentation"],
  },
];

// eslint-disable-next-line react-refresh/only-export-components
export const ADMIN_NAV: NavItem[] = [
  { name: "Admin Overview", href: "/admin", icon: LayoutDashboard },
  // The same page the sidebar calls Platform Analytics. This pointed at
  // `/admin/analytics`, which has no route, so the entry opened an empty admin
  // shell.
  {
    name: "Platform Analytics",
    href: "/admin/platform-analytics",
    icon: BarChart3,
  },
  {
    name: "Organizations",
    href: "/admin/organizations",
    icon: Layers,
    // Rates are set on an organization's detail, so a search for them lands
    // here; the ledger is linked from there rather than listed.
    keywords: [
      "distributors",
      "msps",
      "companies",
      "hierarchy",
      "pricing",
      "rates",
      "margin",
      "discount",
      "markup",
      "resale",
    ],
  },
  {
    name: "Approvals",
    href: "/admin/approvals",
    icon: ClipboardCheck,
    keywords: ["pending", "approve", "reject"],
  },
  { name: "Users", href: "/admin/users", icon: Users },
  {
    name: "Groups",
    href: "/admin/groups",
    icon: Shield,
    keywords: ["permissions", "roles"],
  },
  { name: "Impersonation", href: "/admin/impersonation", icon: UserCog },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // Same rule as the sidebar, from the same hook. Left ungated this offered
  // every administrative page to every role, one keystroke away, while the
  // sidebar beside it was hiding the same rows (TW-45774919). Route guards
  // still refuse anyone who arrives by another route.
  const { visibleNav } = useNavAccess();
  // Memoised like the sidebar's: the lists are static and the filter is cheap,
  // but it ran on every keystroke in the palette.
  const pages = useMemo(() => visibleNav(MAIN_NAV), [visibleNav]);
  const adminPages = useMemo(() => visibleNav(ADMIN_NAV), [visibleNav]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // The scope bar's selection goes with the jump, as it does on the sidebar.
  const carry = useOrgHref();
  const runCommand = useCallback(
    (href: string) => {
      setOpen(false);
      navigate(carry(href));
    },
    [navigate, carry],
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Where do you want to go?" />
      <CommandList>
        <CommandEmpty>No pages found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {pages.map((item) => (
            <CommandItem
              key={item.href}
              value={[item.name, ...(item.keywords ?? [])].join(" ")}
              onSelect={() => runCommand(item.href)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.name}
            </CommandItem>
          ))}
        </CommandGroup>
        {/* The whole group goes when nothing in it is reachable, rather than
            leaving an "Admin" heading over an empty list. */}
        {adminPages.length > 0 && (
          <CommandGroup heading="Admin">
            {adminPages.map((item) => (
              <CommandItem
                key={item.href}
                value={[item.name, ...(item.keywords ?? [])].join(" ")}
                onSelect={() => runCommand(item.href)}
              >
                <item.icon className="mr-2 h-4 w-4" />
                {item.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
